"use client"

import Image from "next/image"
import { useMemo, useState } from "react"
import {
  getOptimizedImageUrl,
  getRetryImageUrl,
  normalizeImageUrl,
} from "@/lib/optimized-image"

type ResilientFillImageProps = {
  src: string | null | undefined
  alt: string
  fallbackText: string
  fallbackClassName?: string
  imageClassName?: string
  sizes: string
  priority?: boolean
  optimizedWidth?: number
}

export function ResilientFillImage({
  src,
  alt,
  fallbackText,
  fallbackClassName = "grid h-full w-full place-items-center bg-zinc-900 font-black text-white/30",
  imageClassName = "object-cover",
  sizes,
  priority = false,
  optimizedWidth = 1200,
}: ResilientFillImageProps) {
  const sourceKey = src?.trim() || ""
  const normalizedSource = normalizeImageUrl(sourceKey)
  const optimizedSource = getOptimizedImageUrl(normalizedSource, {
    width: optimizedWidth,
  })
  const retrySource = getRetryImageUrl(normalizedSource)
  const candidates = useMemo(
    () =>
      Array.from(
        new Set(
          [optimizedSource, normalizedSource, retrySource].filter(
            (candidate): candidate is string => Boolean(candidate),
          ),
        ),
      ),
    [normalizedSource, optimizedSource, retrySource],
  )
  const [failure, setFailure] = useState({ sourceKey: "", candidateIndex: 0 })
  const candidateIndex =
    failure.sourceKey === sourceKey ? failure.candidateIndex : 0
  const activeSource = candidates[candidateIndex] ?? null

  if (!activeSource) {
    return (
      <span className={fallbackClassName} aria-hidden="true">
        {fallbackText}
      </span>
    )
  }

  return (
    <Image
      key={activeSource}
      src={activeSource}
      alt={alt}
      fill
      unoptimized
      priority={priority}
      sizes={sizes}
      className={imageClassName}
      onError={() =>
        setFailure((previous) => ({
          sourceKey,
          candidateIndex:
            previous.sourceKey === sourceKey
              ? previous.candidateIndex + 1
              : 1,
        }))
      }
    />
  )
}
